import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as amplitude from '@amplitude/analytics-browser';

import {
  Badge,
  Button,
  Drawer,
} from "@unified-checkout/design-system";

import {
  ActivityChart,
  ArrowLeft,
  Buildings,
  LoudMegaphone
} from "@unified-checkout/design-system/icons/index";

import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { PAYMOB_PRO_EVENTS } from "@app/dashboard/constants/tracking_events/paymob-pro";
import type { IPaymobProPricingCard } from "@app/dashboard/types/innerLayout/paymob_pro/paymob-pro-pricing-card";

import { PricingCard } from "./PricingCard/PricingCard";
import { getPaymobProPrices } from "../utils";

import styles from "./pricing-drawer.module.scss";

export const PricingDrawer = ({
  isPricingDrawerOpen,
  setIsPricingDrawerOpen,
  setIsPaymobProDrawerOpen
}: {
  isPricingDrawerOpen: boolean;
  setIsPricingDrawerOpen: (open: boolean) => void;
  setIsPaymobProDrawerOpen: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 900;

  const [planType, setPlanType] = useState<"monthly" | "annually">('monthly');

  const localizedPricingPlans = t("innerLayout.paymobPro.pricingPlans", { returnObjects: true }) as Array<IPaymobProPricingCard>;

  const planMetadata = [
    { icon: ActivityChart, iconVariant: "primary" },
    { icon: LoudMegaphone, iconVariant: "violet", hideCheckIcon: true, recommended: true },
    { icon: Buildings as any, hideCheckIcon: true, iconVariant: "success" },
  ];

  const pricingPlans = localizedPricingPlans.map((plan, index) => {
    const localizedPrices = getPaymobProPrices(index);

    return {
      title: plan.title,
      subtitle: plan.subtitle,
      description: plan.description,
      prices: {
        monthly: t(plan.prices.monthly, {
          currency: localizedPrices.currency,
          monthly: localizedPrices.monthly,
        }),
        annually: t(plan.prices.annually, {
          currency: localizedPrices.currency,
          annually: localizedPrices.annually,
        }),
      },
      includes: plan.includes,
      hideCheckIcon: plan.hideCheckIcon,
      ...planMetadata[index],
    };
  });

  const handleCloseDrawer = () => {
    setIsPricingDrawerOpen(false)
  };

  const handleBackDrawer = () => {
    setIsPricingDrawerOpen(false);
    setIsPaymobProDrawerOpen(true);
  };

  useEffect(() => {
    if (isPricingDrawerOpen) {
      amplitude.track(PAYMOB_PRO_EVENTS.PLANS_VIEWED);
    }
  }, [isPricingDrawerOpen]);

  const Header =
    <div className={styles['pricing__drawer__header']}>
      <Button
        variant="link"
        color="primary"
        size="sm"
        leadingIcon={ArrowLeft}
        text={t('innerLayout.paymobPro.pricingBackBtn')}
        onClickHandler={handleBackDrawer}
      />
      <div className={styles['pricing__drawer__header__title-wrapper']}>
        <Badge color="primary" text={t('innerLayout.paymobPro.pricingBadge')} size="md" />
        <div className={styles['pricing__drawer__header__title-wrapper__title']}>
          {t('innerLayout.paymobPro.pricingTitle')}</div>
      </div>
    </div>

  const PricingPlanBtn =
    <div className={styles['pricing__drawer__content__plan-buttons']}>
      <div className={styles['pricing__drawer__content__plan-buttons__btns']}>
        <div
          className={styles['pricing__drawer__content__plan-buttons__btns__monthly']}
          onClick={() => setPlanType('monthly')}
          data-active={planType === 'monthly' && true}
        >
          {t('innerLayout.paymobPro.pricingMonthlyBtn')}
        </div>

        <div
          className={styles['pricing__drawer__content__plan-buttons__btns__annually']}
          onClick={() => setPlanType('annually')}
          data-active={planType === 'annually' && true}
        >
          {t('innerLayout.paymobPro.pricingAnnuallyBtn')}
          <Badge color="green" text={t('innerLayout.paymobPro.pricingAnnuallyBadge')} size="sm" />
        </div>

      </div>
    </div>

  return (
    <Drawer
      open={isPricingDrawerOpen}
      wrapperClassName={styles['pricing__drawer']}
      size={isMobile ? "100%" : "70rem"}
      hasCloseIcon
      isOverlayAboveModal
      onCloseHandler={handleCloseDrawer}
      title={Header}
      supportingText={t('innerLayout.paymobPro.pricingSupportingText')}
    >
      <div className={styles['pricing__drawer__content']}>
        {PricingPlanBtn}

        <div className={styles['pricing__drawer__content__pricing-cards']}>
          {pricingPlans.map((plan, index) => {
            const { title, subtitle, icon, iconVariant, description, prices, includes, recommended, hideCheckIcon } = plan;

            return (
              <PricingCard
                key={index}
                title={title}
                subtitle={subtitle}
                icon={icon}
                iconVariant={iconVariant}
                description={description}
                prices={prices}
                includes={includes}
                planType={planType}
                recommended={recommended}
                hideCheckIcon={hideCheckIcon}
              />
            )
          })}
        </div>

      </div>

    </Drawer>
  );
};
